
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { getPlacesLibrary } from "@/utils/googleMapsLoader";

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  description: string;
}

const PlacesAutocomplete = ({
  value,
  onChange,
  placeholder
}: PlacesAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize Places library
  useEffect(() => {
    const initPlaces = async () => {
      try {
        const placesLib = await getPlacesLibrary();
        placesLibRef.current = placesLib;
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        setIsReady(true);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        toast({
          title: "Error",
          description: "Failed to load location search. Please try again later.",
          variant: "destructive",
        });
      }
    };
    initPlaces();
  }, []);

  // Sync external value changes (only when prop `value` or isReady changes; omit inputValue to avoid resetting user input)
  useEffect(() => {
    if (isReady && value !== inputValue) {
      setInputValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit inputValue: syncing only when parent passes new value
  }, [isReady, value]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async (input: string) => {
    if (!placesLibRef.current || !sessionTokenRef.current || input.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const request: google.maps.places.AutocompleteRequest = {
        input,
        sessionToken: sessionTokenRef.current,
        includedPrimaryTypes: ['locality'],
      };

      const { suggestions: results } = await placesLibRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const formattedSuggestions: Suggestion[] = results.map((result) => {
        const prediction = result.placePrediction;
        const mainText = prediction.mainText?.text || '';
        const secondaryText = prediction.secondaryText?.text || '';
        return {
          placeId: prediction.placeId,
          mainText,
          secondaryText,
          description: mainText + (secondaryText ? ', ' + secondaryText : ''),
        };
      });

      setSuggestions(formattedSuggestions);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    }
  }, []);

  const handleSelect = (description: string) => {
    setInputValue(description);
    onChange(description);
    setSuggestions([]);
    setIsOpen(false);

    // Create new session token for next search
    if (placesLibRef.current) {
      sessionTokenRef.current = new placesLibRef.current.AutocompleteSessionToken();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue === '') {
      onChange('');
      setSuggestions([]);
      setIsOpen(false);
    } else {
      setIsOpen(true);
      onChange(newValue);

      // Debounce the API call
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(newValue);
      }, 300);
    }
  };

  const handleFocus = () => {
    if (inputValue !== '' && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  if (!isReady) {
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`${placeholder} (Loading...)`}
        className="w-full"
        disabled
      />
    );
  }

  return (
    <div className="relative w-full">
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className="w-full"
      />

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              onClick={() => handleSelect(suggestion.description)}
              className="w-full text-left cursor-pointer p-2 hover:bg-accent transition-colors focus:bg-accent focus:outline-none"
            >
              <span className="font-medium">{suggestion.mainText}</span>{" "}
              <span className="text-muted-foreground text-sm">{suggestion.secondaryText}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
